import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { extendedManagementApi } from '../api/managementService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}

export default function ApiClientAnalytics({ isOpen, onClose, clientId, clientName }: Props) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['apiClientDetailedStats', clientId],
    queryFn: () => extendedManagementApi.getClientDetailedStats(clientId),
    enabled: isOpen && !!clientId,
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-5xl rounded-xl bg-white shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Usage Analytics
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">{clientName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">Loading analytics...</div>
              </div>
            ) : stats ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm text-blue-600 font-medium">Total Requests</div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">
                      {stats.totalRequests.toLocaleString()}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">Last 7 days</div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-green-600 font-medium">Avg Response Time</div>
                    <div className="text-2xl font-bold text-green-900 mt-1">
                      {stats.avgResponseTimeMs ? `${Math.round(stats.avgResponseTimeMs)}ms` : 'N/A'}
                    </div>
                    <div className="text-xs text-green-600 mt-1">Average</div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-sm text-red-600 font-medium">Errors</div>
                    <div className="text-2xl font-bold text-red-900 mt-1">
                      {stats.errorBreakdown.reduce((sum, e) => sum + e.count, 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-red-600 mt-1">All error codes</div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="text-sm text-orange-600 font-medium">Rate Limit Hits</div>
                    <div className="text-2xl font-bold text-orange-900 mt-1">
                      {stats.dailyStats.reduce((sum, day) => sum + day.rateLimitHits, 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-orange-600 mt-1">429 responses</div>
                  </div>
                </div>

                {/* Daily Stats Table */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Daily Breakdown</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Requests</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate Limits</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {stats.dailyStats.map((day) => (
                          <tr key={day.date} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900">{formatDate(day.date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">{day.totalRequests.toLocaleString()}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              <span className={day.errors > 0 ? 'text-red-600' : 'text-gray-400'}>
                                {day.errors}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-right">
                              <span className={day.rateLimitHits > 0 ? 'text-orange-600' : 'text-gray-400'}>
                                {day.rateLimitHits}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 text-right">
                              {day.avgResponseTime ? `${Math.round(day.avgResponseTime)}ms` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Top Endpoints */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Endpoints</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Requests</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {stats.topEndpoints.map((endpoint, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900 font-mono">{endpoint.endpoint}</td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">{endpoint.count.toLocaleString()}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              <span className={endpoint.errors > 0 ? 'text-red-600' : 'text-gray-400'}>
                                {endpoint.errors}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 text-right">
                              {endpoint.avgResponseTime ? `${Math.round(endpoint.avgResponseTime)}ms` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Rate Limit Tier Usage */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Rate Limit Tier Usage</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {stats.tierUsage.map((tier) => (
                      <div key={tier.tier} className="border rounded-lg p-3">
                        <div className="text-sm font-medium text-gray-900">{tier.tier || 'Unclassified'}</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">{tier.count.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Avg: {tier.avgResponseTime ? `${Math.round(tier.avgResponseTime)}ms` : 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Error Breakdown */}
                {stats.errorBreakdown.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Error Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {stats.errorBreakdown.map((error) => (
                        <div key={error.statusCode} className="border rounded-lg p-3 bg-red-50">
                          <div className="text-sm font-medium text-red-900">HTTP {error.statusCode}</div>
                          <div className="text-2xl font-bold text-red-600 mt-1">{error.count.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">No analytics data available</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
